import { useState } from "react";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import uk from "date-fns/locale/uk";
import "react-datepicker/dist/react-datepicker.css";
import {
    Box,
    Paper,
    Stack,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    FormControlLabel,
    Switch,
    Button,
    CircularProgress,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";

registerLocale("uk", uk);

const BRAND = "#D72638";

/** Скільки разів має повторитися та сама категорія за місяць, щоб потрапити в «Зведення» */
const MAIN_EXPENSE_MIN_PAYMENTS = 2;

/**
 * Мінімальна сума по категорії за місяць (грн) у «Зведенні» (після об’єднання повторів).
 * За вашим описом — 5000; тоді дрібні повтори (кава ~145 грн) лише в «Докладніше». Для них — 100 або 0.
 */
const MAIN_EXPENSE_MIN_GROUP_UAH = 5000;

const expensesTheme = createTheme({
    palette: {
        primary: { main: BRAND },
        success: { main: "#2e7d32" },
    },
    shape: { borderRadius: 10 },
});

const PEOPLE = [
    { id: "iryna", label: "Іра" },
    { id: "andrii", label: "Нежид" },
    { id: "oleksii", label: "Плеханов" },
    { id: "tatarinova", label: "Татарінова" },
    { id: "smirnova", label: "Смирнова" },
    { id: "shlyahova", label: "Шляхова" },
    { id: "ritz", label: "Ріц" },
    { id: "svitlychna", label: "Світлична" },
    { id: "naumenko", label: "Науменко" },
];

const toggleSelectedSx = {
    textTransform: "none",
    fontWeight: 500,
    px: { xs: 1.25, sm: 2 },
    py: 1,
    borderColor: "divider !important",
    "&.Mui-selected": {
        backgroundColor: `${BRAND} !important`,
        color: "#fff",
        borderColor: `${BRAND} !important`,
        "&:hover": {
            backgroundColor: `${BRAND} !important`,
        },
    },
};

/** MUI за замовчуванням злипає кнопки через margin-left: -1px і border-left: transparent — нам не потрібно при gap і окремих радіусах */
const toggleGroupSeparatedSx = {
    "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
        marginLeft: 0,
        borderLeft: "1px solid",
        borderLeftColor: "divider",
    },
};

function stripLeadingFromDesc(s) {
    return (s || "").replace(/^Від:\s*/i, "").trim();
}

/** Ключ категорії для групування в «Зведенні» (біла картка: description, ФОП: comment) */
function summaryExpenseLabel(p, whiteMode) {
    const desc = stripLeadingFromDesc(p.description);
    const com = (p.comment || "").trim();
    if (whiteMode) return desc || com || "—";
    return com || desc || "—";
}

/** Основні витрати за місяць: сума по категорії, лише повтори та від порогу суми */
function buildMainExpenseSummaryGroups(monoList, whiteMode) {
    const map = new Map();
    for (const t of monoList) {
        if (t.amount >= 0) continue;
        const label = summaryExpenseLabel(t, whiteMode);
        const key = label.toLowerCase().trim() || "—";
        let g = map.get(key);
        if (!g) {
            g = { key, label, sum: 0, count: 0 };
            map.set(key, g);
        }
        g.sum += t.amount;
        g.count += 1;
    }
    return [...map.values()]
        .filter(
            (g) =>
                g.count >= MAIN_EXPENSE_MIN_PAYMENTS &&
                Math.abs(g.sum / 100) >= MAIN_EXPENSE_MIN_GROUP_UAH
        )
        .sort((a, b) => Math.abs(b.sum) - Math.abs(a.sum));
}

export const Expenses = () => {
    const [mono, setMono] = useState([]);
    const [totalMonth, setTotalMonth] = useState(0);
    const [textStatus, setTextStatus] = useState("Оберіть карту");
    const [startDate, setStartDate] = useState(new Date());
    const [isLoading, setLoading] = useState(false);
    const [active, setActive] = useState(null);
    const [activeBtn, setActiveBtn] = useState(false);
    const [cardType, setCardType] = useState("fop");
    const [showIncoming, setShowIncoming] = useState(false);

    const getPayments = (id = "", overrideType = null) => {
        setLoading(true);
        const t = overrideType != null ? overrideType : cardType;
        const firstDay = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
        const lastDay = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);

        fetch(
            `/api/payments/${id}?startDate=${Math.floor(firstDay.setHours(0, 0, 0) / 1000)}&endDate=${Math.floor(lastDay.setHours(23, 59, 59) / 1000)}&type=${t}`
        )
            .then((response) => {
                if (!response.ok) {
                    console.error(`Помилка завантаження ${response.status}`);
                    if (response.status === 500) {
                        alert("Занадто часто, спробуйте пізніше");
                        throw new Error("Something went wrong.");
                    }
                }
                return response.json();
            })
            .then((data) => {
                if (!Array.isArray(data)) {
                    setTextStatus("Помилка відповіді сервера");
                    setMono([]);
                    setTotalMonth(0);
                    return;
                }
                if (data.length === 0) {
                    setTextStatus("Жодної оплати в цей день не було");
                    setMono([]);
                    setTotalMonth(0);
                } else {
                    let sum = 0;
                    data.forEach((payment) => {
                        sum = payment.amount < 0 ? sum + payment.amount / 100 : sum;
                    });
                    setTotalMonth(sum);
                    setMono(data);
                }
            })
            .catch(function (error) {
                console.log("Request failed", error);
                setMono([]);
                setTotalMonth(0);
            })
            .finally(() => setLoading(false));
    };

    const handleCardTypeChange = (_e, next) => {
        if (next == null) return;
        setCardType(next);
        if (active) getPayments(active, next);
    };

    const handlePersonChange = (_e, nextId) => {
        if (nextId == null) return;
        setActive(nextId);
        getPayments(nextId);
        setActiveBtn(false);
    };

    const handleOpenMore = () => setActiveBtn(!activeBtn);

    let amountNovapay = 0;
    let amountFacebook = 0;
    let amountReturns = 0;
    let amountTaxes = 0;

    const isWhiteMode = cardType === "white";

    mono.forEach((t) => {
        amountNovapay =
            (t.description?.toLowerCase() === "novapay" || t.description?.toLowerCase() === "нова пошта") && t.amount < 0
                ? amountNovapay + t.amount / 100
                : amountNovapay;
        amountFacebook = t.description === "Facebook" && t.amount < 0 ? amountFacebook + t.amount / 100 : amountFacebook;
        amountReturns =
            t.comment !== undefined && t.comment.toLowerCase() === "повернення коштів" && t.amount < 0
                ? amountReturns + t.amount / 100
                : amountReturns;
        amountTaxes =
            t.comment !== undefined &&
            t.comment.toLowerCase().includes("101") &&
            t.comment.toLowerCase().includes("року") &&
            t.amount < 0
                ? amountTaxes + t.amount / 100
                : amountTaxes;
    });

    const summaryMainGroups = buildMainExpenseSummaryGroups(mono, isWhiteMode);
    const hasAnyOutgoing = mono.some((t) => t.amount < 0);

    const detailedRows = showIncoming ? mono : mono.filter((p) => p.amount < 0);

    return (
        <ThemeProvider theme={expensesTheme}>
        <Box component="main" className="expenses-page" sx={{ py: { xs: 2, md: 3 }, px: { xs: 2, sm: 3 }, maxWidth: 1100, mx: "auto" }}>
            <Typography variant="h5" component="h1" fontWeight={700} sx={{ mb: 2, letterSpacing: "-0.02em" }}>
                Оплати
            </Typography>

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, sm: 2.5, md: 3 },
                    mb: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                }}
            >
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Тип рахунку
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    value={cardType}
                    onChange={handleCardTypeChange}
                    aria-label="Тип рахунку"
                    sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        flexWrap: "nowrap",
                        gap: 1,
                        "& .MuiToggleButtonGroup-grouped": {
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: "10px !important",
                            m: 0,
                            flex: { xs: "0 0 auto", sm: "1 1 0" },
                            width: { xs: "100%", sm: "auto" },
                            minWidth: 0,
                        },
                        ...toggleGroupSeparatedSx,
                    }}
                >
                    <ToggleButton value="fop" sx={toggleSelectedSx}>
                        ФОП рахунок
                    </ToggleButton>
                    <ToggleButton value="white" sx={toggleSelectedSx}>
                        Біла картка
                    </ToggleButton>
                    <ToggleButton value="black" sx={toggleSelectedSx}>
                        Чорна картка (UAH)
                    </ToggleButton>
                </ToggleButtonGroup>

                <FormControlLabel
                    control={
                        <Switch checked={showIncoming} onChange={(e) => setShowIncoming(e.target.checked)} color="primary" />
                    }
                    label="Показувати надходження у списку «Докладніше» (зеленим)"
                    sx={{ mt: 2, alignItems: "flex-start", ml: 0, display: "flex" }}
                />

                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2.5, mb: 1, fontWeight: 600 }}>
                    Оберіть людину
                </Typography>
                <Stack
                    direction="row"
                    className="expenses-people-scroll"
                    sx={{
                        overflowX: { xs: "auto", md: "visible" },
                        flexWrap: { xs: "nowrap", md: "wrap" },
                        gap: 1,
                        pb: 0.5,
                    }}
                >
                    <ToggleButtonGroup
                        exclusive
                        value={active}
                        onChange={handlePersonChange}
                        aria-label="Оберіть людину"
                        sx={{
                            flexWrap: { xs: "nowrap", md: "wrap" },
                            gap: 1,
                            "& .MuiToggleButtonGroup-grouped": {
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: "10px !important",
                                m: 0,
                                flexShrink: 0,
                            },
                            ...toggleGroupSeparatedSx,
                        }}
                    >
                        {PEOPLE.map((p) => (
                            <ToggleButton key={p.id} value={p.id} sx={toggleSelectedSx}>
                                {p.label}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Stack>

                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2.5, mb: 1, fontWeight: 600 }}>
                    Місяць обліку
                </Typography>
                <Box className="holder-datapickers">
                    <DatePicker
                        locale="uk"
                        selected={startDate}
                        onChange={(date) => date && setStartDate(date)}
                        dateFormat="dd.MM.yyyy"
                    />
                </Box>
            </Paper>

            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, sm: 2.5, md: 3 },
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    minHeight: 200,
                }}
            >
                {isLoading ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                        <CircularProgress color="primary" />
                    </Stack>
                ) : (
                    <Stack spacing={2}>
                        {isWhiteMode ? (
                            <Stack spacing={0.5}>
                                <Typography variant="body2" color="text.secondary">
                                    Витрати на Facebook:{" "}
                                    <Typography component="span" fontWeight={700} color="text.primary">
                                        {Math.floor(amountFacebook).toLocaleString("uk-UA")}
                                    </Typography>{" "}
                                    грн.
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Витрати на Novapay:{" "}
                                    <Typography component="span" fontWeight={700} color="text.primary">
                                        {Math.floor(amountNovapay).toLocaleString("uk-UA")}
                                    </Typography>{" "}
                                    грн.
                                </Typography>
                            </Stack>
                        ) : (
                            <Stack spacing={0.5}>
                                <Typography variant="body2" color="text.secondary">
                                    Повернення коштів:{" "}
                                    <Typography component="span" fontWeight={700} color="text.primary">
                                        {Math.floor(Math.abs(amountReturns)).toLocaleString("uk-UA")}
                                    </Typography>{" "}
                                    грн.
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Податки:{" "}
                                    <Typography component="span" fontWeight={700} color="text.primary">
                                        {Math.floor(Math.abs(amountTaxes)).toLocaleString("uk-UA")}
                                    </Typography>{" "}
                                    грн.
                                </Typography>
                            </Stack>
                        )}

                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            Зведення
                        </Typography>
                        {totalMonth === 0 && !hasAnyOutgoing ? (
                            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                                {textStatus}
                            </Typography>
                        ) : summaryMainGroups.length === 0 ? (
                            <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                                Немає категорій за цими правилами. Усі списання — у «Докладніше».
                            </Typography>
                        ) : (
                            <Stack spacing={1}>
                                {summaryMainGroups.map((g, i) => (
                                    <Paper
                                        key={g.key}
                                        variant="outlined"
                                        sx={{
                                            p: 1.25,
                                            borderRadius: 2,
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap">
                                            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ minWidth: "1.75rem" }}>
                                                #{i + 1}
                                            </Typography>
                                            <Typography component="div" sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography component="span" fontWeight={700} sx={{ color: BRAND }}>
                                                    {Math.abs(g.sum / 100).toLocaleString("uk-UA")} грн
                                                </Typography>
                                                <Typography component="span" variant="body2" color="text.primary" sx={{ wordBreak: "break-word", pl: 0.75 }}>
                                                    {g.label}
                                                </Typography>
                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                                                    ×{g.count}
                                                </Typography>
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                ))}
                            </Stack>
                        )}

                        <Box>
                            <Button
                                variant="outlined"
                                onClick={handleOpenMore}
                                sx={{
                                    mt: 1,
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    borderColor: BRAND,
                                    color: activeBtn ? "#fff" : BRAND,
                                    bgcolor: activeBtn ? BRAND : "transparent",
                                    "&:hover": {
                                        borderColor: BRAND,
                                        bgcolor: activeBtn ? BRAND : "rgba(215, 38, 56, 0.06)",
                                    },
                                }}
                            >
                                Докладніше
                            </Button>
                        </Box>

                        {activeBtn && (
                            <Stack spacing={1} sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                    Деталізація
                                </Typography>
                                {detailedRows.length === 0 && totalMonth === 0 ? (
                                    <Typography color="text.secondary">{textStatus}</Typography>
                                ) : (
                                    detailedRows.map((payment) => {
                                        const v = "Від:";
                                        const t = "Транз.рахунок платежi_ DN, DG, DZ";
                                        const rExp = new RegExp(v, "g");
                                        let name = payment.description.replace(rExp, "");
                                        name = name.includes(t) ? payment.comment.split(",")[1] : name;
                                        const time = payment.date;

                                        const date = new Date(payment.time * 1000);
                                        const yyyy = date.getFullYear();
                                        let mm = date.getMonth() + 1;
                                        let dd = date.getDate();

                                        if (dd < 10) dd = "0" + dd;
                                        if (mm < 10) mm = "0" + mm;

                                        const formattedDate = dd + "-" + mm + "-" + yyyy;
                                        const isIncoming = payment.amount > 0;

                                        return (
                                            <Paper
                                                key={payment.id}
                                                variant="outlined"
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    borderLeft: "4px solid",
                                                    borderLeftColor: isIncoming ? "success.main" : "divider",
                                                    bgcolor: isIncoming ? "rgba(46, 125, 50, 0.08)" : "transparent",
                                                }}
                                            >
                                                <Stack spacing={1}>
                                                    <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {formattedDate}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {time}
                                                        </Typography>
                                                        <Typography fontWeight={700} color={isIncoming ? "success.main" : "text.primary"}>
                                                            {payment.amount / 100} грн
                                                        </Typography>
                                                    </Stack>
                                                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                                                        {payment.comment}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                                                        {name}
                                                    </Typography>
                                                </Stack>
                                            </Paper>
                                        );
                                    })
                                )}
                            </Stack>
                        )}
                    </Stack>
                )}
            </Paper>
        </Box>
        </ThemeProvider>
    );
};
